import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { erApiFeil } from '../api/feil.ts';
import { useMe } from '../api/useMe.ts';
import { INNSPILL } from '../skall/rom.ts';
import { tilgangFra } from '../skall/tilgang.ts';
import { useInnstillinger, type ReasonCode, type ReplyTemplate } from '../api/useSettings.ts';
import { useInboxCounts, useLeggTilNotat, useNyeSaker, useOppdaterSak, useSak, useSaker, useFjernVedlegg, useSkjermbildeUrl, type SakFilter, type StatusType, type SubmissionStatus, type SubmissionType, type SubmissionUpdate } from '../api/useSubmissions.ts';
import { Ikon } from '../status/Ikon.tsx';
import { Statusflyt } from './Statusflyt.tsx';
import { KATEGORI, KATEGORIER, LESER_GRUNN, VALGBARE, kanGåTil, statusEtikett, statusOppforing } from '../status/status.ts';
import { Detalj, kjentFeilGrunn } from './Detalj.tsx';
import { Rad } from './Rad.tsx';
import { saksnr, tolkSaksnummer } from './saksnummer.ts';
import { Kartknapp } from './Kartknapp.tsx';
import { NyeStripe, RekkefolgeVelger, sortertTittel, useRekkefolge } from './Rekkefolge.tsx';
import { KART } from './Tastaturkart.tsx';
import { Tomrute } from './Tomrute.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { ANGRE_SEKUNDER, Toast, type ToastInnhold } from './Toast.tsx';
import { useSakGrunnlag } from './SakGrunnlag.tsx';
import { useKonfig } from '../konfigKontekst.tsx';

/** DD-10: sammenslåingen sendes først når angre-vinduet er ute. Tallet eies av Toast.tsx. */
export { ANGRE_SEKUNDER };

/** Datoen fra datofeltet som et tidspunkt: midt på dagen, lokal tid — «innen 3. desember» betyr hele dagen. */
export function fristDato(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y ?? NaN, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
}

/** Tid en `s`-akkord venter på bokstaven. */
const AKKORD_MS = 1500;

/**
 * Det som er skrevet eller valgt på en sak og ikke sendt (DD-65). Ligger i modulen, ikke i
 * komponenten: det overlever radbytte og å gå til Kjente feil og tilbake, men ikke en ny
 * innlasting. Aldri i localStorage — lista over hva panelet får lagre der er lukket (M2.6).
 */
interface Utkast {
  valgtStatus: SubmissionStatus | null;
  kandidat: string | null;
  notat: string;
  /** Notatfeltet er utfoldet. Svaret er åpent som standard (F17); notatet er det som åpnes. */
  notatAapent: boolean;
  fixedIn: string;
  svarAapent: boolean;
  svarTekst: string;
  mal: ReplyTemplate | null;
  kode: ReasonCode | '';
  frist: string;
}
// `svarAapent: true` er F17: svaret til innsenderen er hovedhandlingen på flaten og møter
// saksbehandleren åpent. Notatet er internt og folder seg ut på forespørsel — et svar
// skrevet i notatfeltet når ingen, og ingen oppdager det.
const TOMT_UTKAST: Utkast = { valgtStatus: null, kandidat: null, notat: '', notatAapent: false, fixedIn: '', svarAapent: true, svarTekst: '', mal: null, kode: '', frist: '' };
/** Et utkast er noe brukeren har skrevet eller valgt. Et åpent, tomt svarfelt alene er ikke det. */
const harInnhold = (u: Utkast) => Boolean(u.notat.trim() || u.svarTekst.trim() || u.valgtStatus || u.fixedIn.trim() || u.frist || u.kode || u.kandidat);
const utkast = new Map<string, Utkast>();

export type Modus = 'innboks' | 'saker';

/**
 * Innboksen (modus «innboks», DD-01: statusType reviewing) og Saker (modus «saker», alle
 * statuser med kategorifilter) — samme liste, samme detalj, samme tastatur (DD-11).
 * Tellingen ER forsiden; tomtilstanden inviterer (DD-19); ingen sak forlater innboksen uten
 * status (DD-26); sammenslåing er manuell med angre (DD-10, DD-24).
 */
export function SakerSide({ modus }: { modus: Modus }) {
  const me = useMe();
  // Rom-rollen i rommet siden hører til — aldri `role`, som bare sier eier eller medlem (M4.8, `skall/tilgang.ts`).
  const kanSkrive = tilgangFra(me.data).kanSkrive(INNSPILL);
  const [kategori, setKategori] = useState<StatusType | undefined>(undefined);
  const [spor, setSpor] = useState<SubmissionType | undefined>(undefined);
  const [versjon, setVersjon] = useState('');
  // H3.1: `/saker?nr=10042` åpner saken fra grunnlaget på en hypotese — samme oppslag som søket.
  const [sok, setSok] = useState(() => (modus === 'saker' ? (new URLSearchParams(window.location.search).get('nr') ?? '') : ''));
  // R4.4: under 900 px er søket et ikon til det brukes (konseptets skjerm 6, samme mønster som
  // Kjente feil). Lukkes det, tømmes strengen — et søk man ikke ser er et filter man ikke vet
  // om. Over grensen står feltet åpent som før, og disse to rører ingenting.
  const [sokAapen, setSokAapen] = useState(() => sok !== '');
  const sokRef = useRef<HTMLInputElement | null>(null);
  // «⚠ N over frist» i toppen er et filter (R1.3, F13) — kontraktens `overdue`, som fantes.
  const [overFrist, setOverFrist] = useState(false);
  // Saksnummeret er et tall fra 10001. «Sak 10042» og «#10042» tolkes her; noe som ikke er et
  // saksnummer sendes ikke, og lista står som den var med en setning om hva som mangler.
  const tolket = tolkSaksnummer(sok);
  const oppslag = typeof tolket === 'number' ? tolket : undefined;
  const ugyldigSok = tolket === null;
  // R3.3: «Ja, jeg også»-tallet i kjente feils detaljrute lenker hit med feilens id. Filteret
  // er et ledd i filterlinja med en vei ut — aldri en tilstand uten avkrysning.
  const [params, setParams] = useSearchParams();
  const kjentFeil = modus === 'saker' ? params.get('kjentFeil') : null;
  // M4.10: «Samme innsender» på saken lenker hit med SAKENS id (aldri pseudonymet). Samme form som
  // kjent feil-leddet: bare i Saker, et ledd i filterlinja med en vei ut. Tjeneren ignorerer
  // statusfilteret for det — innsenderens saker uansett hvor de står.
  const sammeInnsender = modus === 'saker' ? params.get('sameSubjectAs') : null;
  // DD-74: «Frist først · Nyest først» i adressen. Settes bare i filteret for «nyest», så nøkkelen
  // for fristrekkefølgen er den samme som før valget fantes.
  const [sort, setSort] = useRekkefolge();
  const filter = useMemo<SakFilter>(() => {
    const base: SakFilter = modus === 'innboks' ? { statusType: 'reviewing', ...(overFrist ? { overdue: true } : {}) } : kategori ? { statusType: kategori } : {};
    // Reach filtrert på versjon er «kun dette segmentet», aldri en vekt (M4.2, A12).
    const medVersjon = modus === 'saker' && versjon.trim() ? { ...base, appVersion: versjon.trim() } : base;
    const medSpor = spor ? { ...medVersjon, type: spor } : medVersjon;
    const medKjentFeil = kjentFeil ? { ...medSpor, knownIssueId: kjentFeil } : medSpor;
    const medInnsender = sammeInnsender ? { ...medKjentFeil, sameSubjectAs: sammeInnsender } : medKjentFeil;
    const medSort: SakFilter = sort === 'nyest' ? { ...medInnsender, sort: 'nyest' } : medInnsender;
    return oppslag !== undefined ? { ...medSort, number: oppslag } : medSort;
  }, [modus, kategori, versjon, spor, oppslag, overFrist, kjentFeil, sammeInnsender, sort]);

  const liste = useSaker(filter);
  const counts = useInboxCounts();
  const saker = useMemo(() => liste.data?.pages.flatMap((p) => p.items) ?? [], [liste.data]);
  // Stripa (DD-74): ikke mens søket slår opp ett nummer — da er lista ett svar, ikke en kø.
  const nye = useNyeSaker(filter, { rader: saker, hentet: liste.dataUpdatedAt }, liste.isSuccess && oppslag === undefined && !ugyldigSok);

  const [valgtId, setValgtId] = useState<string | null>(null);
  const smal = useSmal();
  const [valgtStatus, setValgtStatus] = useState<SubmissionStatus | null>(null);
  const [kandidat, setKandidat] = useState<string | null>(null);
  const [notat, setNotat] = useState('');
  const [notatAapent, setNotatAapent] = useState(TOMT_UTKAST.notatAapent);
  const [fixedIn, setFixedIn] = useState('');
  const [svarAapent, setSvarAapent] = useState(TOMT_UTKAST.svarAapent);
  const [svarTekst, setSvarTekst] = useState('');
  const [mal, setMal] = useState<ReplyTemplate | null>(null);
  const [kode, setKode] = useState<ReasonCode | ''>('');
  const [frist, setFrist] = useState('');
  const [toast, setToast] = useState<ToastInnhold | null>(null);
  const notatRef = useRef<HTMLTextAreaElement | null>(null);
  const svarRef = useRef<HTMLTextAreaElement | null>(null);
  const innstillinger = useInnstillinger();
  const maler = useMemo(() => innstillinger.data?.replyTemplates ?? [], [innstillinger.data]);
  const akkord = useRef<number | null>(null);
  const venterSammenslaaing = useRef<{ timer: ReturnType<typeof setTimeout>; teller: ReturnType<typeof setInterval> } | null>(null);

  const sak = useSak(valgtId);
  const oppdater = useOppdaterSak();
  const leggTilNotat = useLeggTilNotat();
  const skjermbilde = useSkjermbildeUrl();
  const fjernVedlegg = useFjernVedlegg();
  const konfig = useKonfig();
  const [bildeUrl, setBildeUrl] = useState<string | null>(null);
  const opptatt = oppdater.isPending || leggTilNotat.isPending;
  const nå = useMemo(() => new Date(), [saker]); // eslint-disable-line react-hooks/exhaustive-deps

  // Første sak velges når lista kommer; en valgt sak som forsvinner fra lista erstattes.
  // Under breakpointen (DD-51) er lista det brukeren ser først: ingenting autovelges, og en
  // sak som forsvinner sender tilbake til lista — «lagre og neste» velger neste selv.
  useEffect(() => {
    if (saker.length === 0) {
      if (valgtId !== null) setValgtId(null);
      return;
    }
    if (valgtId === null) {
      if (!smal) setValgtId(saker[0]!.id);
      return;
    }
    if (!saker.some((s) => s.id === valgtId)) setValgtId(smal ? null : saker[0]!.id);
  }, [saker, valgtId, smal]);

  // Ny sak: det som var skrevet på forrige sak legges til side som utkast, og utkastet for
  // saken som velges hentes fram (DD-65). Tekst mistes aldri ved radbytte — produkteier
  // skrev et svar, klikket en annen rad og så det forsvinne (brukerreisene 2026-09-15, F19).
  const gjeldende = useRef<Utkast>(TOMT_UTKAST);
  gjeldende.current = { valgtStatus, kandidat, notat, notatAapent, fixedIn, svarAapent, svarTekst, mal, kode, frist };
  const forrigeId = useRef<string | null>(null);
  useEffect(() => {
    const fra = forrigeId.current;
    if (fra && fra !== valgtId) {
      if (harInnhold(gjeldende.current)) utkast.set(fra, gjeldende.current);
      else utkast.delete(fra);
    }
    forrigeId.current = valgtId;
    const u = (valgtId ? utkast.get(valgtId) : undefined) ?? TOMT_UTKAST;
    setValgtStatus(u.valgtStatus);
    setKandidat(u.kandidat);
    setNotat(u.notat);
    setNotatAapent(u.notatAapent);
    setFixedIn(u.fixedIn);
    setSvarAapent(u.svarAapent);
    setSvarTekst(u.svarTekst);
    setMal(u.mal);
    setKode(u.kode);
    setFrist(u.frist);
  }, [valgtId]);
  // Og når siden forlates (Kjente feil og tilbake): utkastet for saken som står åpen.
  useEffect(
    () => () => {
      const id = forrigeId.current;
      if (!id) return;
      if (harInnhold(gjeldende.current)) utkast.set(id, gjeldende.current);
      else utkast.delete(id);
    },
    [],
  );

  useEffect(() => {
    if (sokAapen) sokRef.current?.focus();
  }, [sokAapen]);

  const aapneSvar = useCallback(() => {
    setSvarAapent(true);
    setTimeout(() => svarRef.current?.focus(), 0);
  }, []);

  // Notatet folder seg ut og tar markøren. Feltet kan alt stå åpent — saken har notater fra
  // før, eller `n` er trykket en gang til — og da er dette bare et fokushopp.
  const aapneNotat = useCallback(() => {
    setNotatAapent(true);
    setTimeout(() => notatRef.current?.focus(), 0);
  }, []);

  const visFeil = useCallback((e: unknown) => {
    setToast({ tekst: erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.', feil: true });
  }, []);

  const nesteEtter = useCallback(
    (fra: string | null) => {
      const i = saker.findIndex((s) => s.id === fra);
      return (saker[i + 1] ?? saker[i - 1] ?? null)?.id ?? null;
    },
    [saker],
  );

  const velgNeste = useCallback((fra: string | null) => setValgtId(nesteEtter(fra)), [nesteEtter]);


  const flytt = useCallback(
    (retning: 1 | -1) => {
      if (saker.length === 0) return;
      const i = saker.findIndex((s) => s.id === valgtId);
      const neste = saker[Math.min(saker.length - 1, Math.max(0, i + retning))];
      if (neste) setValgtId(neste.id);
    },
    [saker, valgtId],
  );

  const detalj = sak.data;
  // H3.1: saken som grunnlag for en hypotese — raden, lenken og velgeren. Toasten er sidens.
  const grunnlag = useSakGrunnlag({ sak: detalj ?? null, kanSkrive, meld: setToast });
  const aapneGrunnlag = grunnlag.aapne;

  // Saksnummeret til en sak panelet bare har id-en til (neste i lista, målet for en
  // sammenslåing): fra lista, saken som er åpen eller dens «ligner på»-kandidater.
  const nrAv = useCallback(
    (id: string): string => {
      const nummer = saker.find((s) => s.id === id)?.number ?? (detalj?.id === id ? detalj.number : undefined) ?? detalj?.dedupeCandidates?.find((k) => k.id === id)?.number;
      return nummer === undefined ? 'saken' : saksnr(nummer);
    },
    [saker, detalj],
  );
  const harSvar = svarTekst.trim() !== '';
  const malUendret = mal !== null && svarTekst.trim() === mal.text.trim();
  const statusEndres = valgtStatus !== null && valgtStatus !== detalj?.status;

  // Kortet frist: en dato i fremtiden, tidligere enn den som gjelder (K39, DD-28).
  const fristGrunn = (() => {
    if (!frist || !detalj) return null;
    const valgt = fristDato(frist);
    if (Number.isNaN(valgt.getTime())) return 'Ugyldig dato';
    if (valgt.getTime() <= Date.now()) return 'Fristen må være i fremtiden';
    const gjeldende = detalj.deadlineAt ? new Date(detalj.deadlineAt).getTime() : Date.now() + (innstillinger.data?.reviewDeadlineDays ?? 90) * 86_400_000;
    if (valgt.getTime() >= gjeldende) return 'Fristen kan kortes, aldri forlenges';
    return null;
  })();

  // DD-26: ingen sak forlater innboksen uten status. Et svar, et notat eller en kortet frist
  // alene er lov — også på en sak i Mottatt (presisert 2026-09-15) — men saken blir liggende:
  // «kan du fortelle mer?» er ikke en avgjørelse, og knappen går ikke videre til neste sak.
  const sperreGrunn = !detalj
    ? 'Ingen sak valgt'
    : !kanSkrive
      ? LESER_GRUNN
      : valgtStatus === 'avvist'
        ? !kode
          ? 'Velg en grunn for avslaget først'
          : !harSvar
            ? 'Skriv en begrunnelse til innsenderen'
            : malUendret
              ? 'Skriv om malen før du sender avslaget'
              : null
        : !statusEndres && !harSvar && !frist && !notat.trim()
          ? detalj.status === 'ny'
            ? 'Velg neste steg, skriv et svar eller et notat'
            : 'Velg neste steg, skriv et svar eller kort fristen'
          : harSvar && detalj.reply
              ? 'Saken har allerede fått svar'
              : valgtStatus === 'levert' && detalj.type === 'feil' && !fixedIn.trim()
                ? 'Oppgi versjonen den er rettet i'
                : fristGrunn;

  // Knappen sier hva den gjør: videre til neste sak bare når saken får status (DD-26, F20).
  const lagreEtikett = statusEndres ? (valgtStatus === 'avvist' ? 'Send avslag og neste' : 'Lagre og neste') : harSvar ? 'Send svar' : notat.trim() || frist ? 'Lagre' : 'Lagre og neste';

  // Fristen som gjelder om Vurderes velges nå: sakens egen, ellers leietakerens standard fra i dag (DD-56 viser den i løftet).
  const fristVedVurderes = !detalj ? null : frist && !fristGrunn ? fristDato(frist).toISOString() : (detalj.deadlineAt ?? new Date(Date.now() + (innstillinger.data?.reviewDeadlineDays ?? 90) * 86_400_000).toISOString());

  // «Slå sammen» står på hver kandidatrad (R1.4): grunnen gjelder raden, ikke et valg som
  // mangler. Tastaturveien er uendret — 1–3 velger, `m` slår sammen den valgte.
  const slaaSammenGrunn = useCallback(
    (kandidatId: string | null) =>
      !detalj
      ? 'Ingen sak valgt'
      : !kanSkrive
        ? LESER_GRUNN
        : detalj.status !== 'ny'
          ? `Kan bare slås sammen fra ${statusEtikett('ny', detalj.type)}`
          : !kandidatId
            ? 'Velg en kandidat (1–3) først'
            : venterSammenslaaing.current
              ? 'En sammenslåing venter alt'
              : null,
    [detalj, kanSkrive],
  );

  // F29 (2026-09-18): samme lagring to veier. «Lagre og neste» er køarbeidet (DD-11);
  // «Lagre» lar saken stå valgt for den som følger én sak gjennom flere steg — tre søk på
  // saksnummeret var prisen før. Validering, sperregrunn og tømming er identiske.
  const lagre = useCallback(async (gaaVidere: boolean) => {
    if (!detalj || sperreGrunn) return;
    const body: SubmissionUpdate = {
      ...(statusEndres && valgtStatus ? { status: valgtStatus } : {}),
      ...(valgtStatus === 'levert' && fixedIn.trim() ? { fixedIn: fixedIn.trim() } : {}),
      ...(harSvar ? { reply: { text: svarTekst.trim(), ...(valgtStatus === 'avvist' && kode ? { reasonCode: kode } : {}), ...(mal ? { templateId: mal.id } : {}) } } : {}),
      ...(frist ? { deadlineAt: fristDato(frist).toISOString() } : {}),
    };
    try {
      if (notat.trim()) await leggTilNotat.mutateAsync({ id: detalj.id, text: notat.trim() });
      if (Object.keys(body).length > 0) await oppdater.mutateAsync({ id: detalj.id, body });
      utkast.delete(detalj.id);
      // Feltene tømmes FØR neste sak velges: ellers legger radbyttet det som nettopp ble sendt
      // tilbake som utkast. Uten status blir saken stående, og svaret vises nå som sendt.
      setValgtStatus(null);
      setKandidat(null);
      setNotat('');
      setFixedIn('');
      setSvarTekst('');
      setSvarAapent(false);
      setMal(null);
      setKode('');
      setFrist('');
      const tillegg = [harSvar ? 'svar sendt til innsender' : null, notat.trim() ? 'notat lagret' : null, frist ? 'fristen kortet' : null].filter(Boolean);
      if (statusEndres && valgtStatus && gaaVidere) {
        // Videre til neste sak — og kvitteringen sier det, så det ikke ser ut som teksten forsvant (F20).
        const neste = nesteEtter(detalj.id);
        setToast({ tekst: `${saksnr(detalj.number)} → ${statusEtikett(valgtStatus, detalj.type)}${tillegg.length ? ` — ${tillegg.join(', ')}` : ''}.${neste ? ` Viser neste sak: ${nrAv(neste)}.` : ''}` });
        velgNeste(detalj.id);
      } else if (statusEndres && valgtStatus) {
        // «Lagre» (F29): saken blir stående. Forlater den innboksen, sier kvitteringen hvor den er.
        const uteAvInnboksen = modus === 'innboks' && statusOppforing(valgtStatus).statusType !== 'reviewing';
        setToast({ tekst: `${saksnr(detalj.number)} → ${statusEtikett(valgtStatus, detalj.type)}${tillegg.length ? ` — ${tillegg.join(', ')}` : ''}.${uteAvInnboksen ? ' Saken er ute av innboksen — den ligger i Saker.' : ''}` });
      } else {
        // Uten status blir saken liggende (DD-26). Feltene tømmes; svaret står nå som sendt i saken.
        const første = tillegg.join(', ');
        setToast({ tekst: `${første.charAt(0).toUpperCase()}${første.slice(1)}. ${saksnr(detalj.number)} blir liggende i innboksen til den har fått status.` });
      }
    } catch (e) {
      visFeil(e);
    }
  }, [detalj, sperreGrunn, statusEndres, valgtStatus, fixedIn, harSvar, svarTekst, kode, mal, frist, notat, leggTilNotat, oppdater, velgNeste, visFeil, nesteEtter, nrAv, modus]);
  const lagreOgNeste = useCallback(() => lagre(true), [lagre]);
  const lagreBliStaaende = useCallback(() => lagre(false), [lagre]);

  const avbrytSammenslaaing = useCallback(() => {
    const v = venterSammenslaaing.current;
    if (!v) return;
    clearTimeout(v.timer);
    clearInterval(v.teller);
    venterSammenslaaing.current = null;
    setToast(null);
  }, []);

  const slaaSammen = useCallback((kandidatId?: string) => {
    const maal = kandidatId ?? kandidat;
    if (!detalj || !maal || slaaSammenGrunn(maal)) return;
    const kilde = detalj.id;
    let sekunder = ANGRE_SEKUNDER;
    const vis = () =>
      setToast({
        tekst: `Lagt til som «Ja, jeg også» på ${nrAv(maal)}. Teksten er tatt vare på som internt notat. Ingen varsel sendes nå.`,
        sekunder,
        angre: avbrytSammenslaaing,
      });
    vis();
    const teller = setInterval(() => {
      sekunder -= 1;
      if (sekunder > 0) vis();
    }, 1000);
    const timer = setTimeout(() => {
      clearInterval(teller);
      venterSammenslaaing.current = null;
      oppdater
        .mutateAsync({ id: kilde, body: { status: 'duplikat', canonicalId: maal } })
        .then(() => {
          setToast({ tekst: `${nrAv(kilde)} er slått sammen inn i ${nrAv(maal)}.` });
          velgNeste(kilde);
        })
        .catch(visFeil);
    }, ANGRE_SEKUNDER * 1000);
    venterSammenslaaing.current = { timer, teller };
  }, [detalj, slaaSammenGrunn, kandidat, oppdater, velgNeste, visFeil, avbrytSammenslaaing, nrAv]);

  useEffect(() => () => avbrytSammenslaaing(), [avbrytSammenslaaing]);

  /**
   * Skjermbildet hentes på en EKSPLISITT handling, aldri automatisk: hvert kall logges som
   * `attachment.opened`, og det er derfor URL-en er et eget endepunkt og ikke et felt i
   * detaljen. Ville bildet vist seg av seg selv, logget hver saksvisning en åpning.
   *
   * Er `attachmentOrigin` satt, vises bildet i panelet; ellers åpnes det i ny fane som før.
   * Se «Skjermbildet vises i panelet» i tech-decisions.md.
   */
  const aapneSkjermbilde = useCallback(() => {
    if (!detalj?.hasAttachment) return;
    // Er bildet oppe, skjuler samme handling det igjen — UTEN et nytt kall. Knappen og
    // `o` deler denne, så de kan ikke oppføre seg ulikt; første utkast toggla bare
    // knappen, og da hentet `o` på nytt og logget en åpning til.
    if (bildeUrl) {
      setBildeUrl(null);
      return;
    }
    skjermbilde
      .mutateAsync(detalj.id)
      .then((url) => {
        if (konfig.attachmentOrigin) setBildeUrl(url);
        else window.open(url, '_blank', 'noopener');
      })
      .catch(visFeil);
  }, [detalj, skjermbilde, visFeil, konfig.attachmentOrigin, bildeUrl]);

  /**
   * «Fjern bildet» etter bekreftelsen i detaljen. Et bilde som er oppe, lukkes med det samme —
   * URL-en peker på et objekt som ikke finnes lenger. Feiler kallet, står bildet, og feilen vises.
   */
  const fjernBilde = useCallback(async () => {
    if (!detalj?.hasAttachment || !kanSkrive) return;
    try {
      await fjernVedlegg.mutateAsync(detalj.id);
      setBildeUrl(null);
      setToast({ tekst: `Bildet er fjernet fra ${saksnr(detalj.number)}. Innsenderen ser at det er fjernet, ikke hvorfor.` });
    } catch (e) {
      visFeil(e);
    }
  }, [detalj, kanSkrive, fjernVedlegg, visFeil]);

  // Bildet hører til saken som er valgt. Bytter man sak, skal det forrige ikke bli stående.
  useEffect(() => setBildeUrl(null), [detalj?.id]);

  const bekreftLenke = useCallback(() => {
    if (!detalj?.relatedKnownIssue || !kanSkrive || detalj.status !== 'ny') return;
    oppdater
      .mutateAsync({ id: detalj.id, body: { knownIssueId: detalj.relatedKnownIssue.id } })
      .then(() => setToast({ tekst: `${saksnr(detalj.number)} bekreftet som samme som «${detalj.relatedKnownIssue!.title}» — teller nå som «Ja, jeg også». Ingen varsel sendes nå.` }))
      .catch(visFeil);
  }, [detalj, kanSkrive, oppdater, visFeil]);

  const fjernLenke = useCallback(() => {
    if (!detalj?.relatedKnownIssue || !kanSkrive) return;
    oppdater.mutateAsync({ id: detalj.id, body: { relatedKnownIssueId: null } }).catch(visFeil);
  }, [detalj, kanSkrive, oppdater, visFeil]);

  // «Gjør til kjent feil» (M2.4): registeret åpnes med saken forhåndsutfylt; lenkingen skjer ved lagring der.
  const naviger = useNavigate();
  const gjoerTilKjentFeil = useCallback(() => {
    if (!detalj || kjentFeilGrunn(detalj, kanSkrive)) return;
    void naviger(`/kjente-feil?fraSak=${encodeURIComponent(detalj.id)}`);
  }, [detalj, kanSkrive, naviger]);

  // DD-11: tastaturet bærer hele flyten. Ikke i felter, unntatt Ctrl+Enter og Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mål = e.target as HTMLElement | null;
      const iFelt = mål !== null && /^(INPUT|TEXTAREA|SELECT)$/.test(mål.tagName);
      if (e.key === 'Enter' && e.ctrlKey) {
        e.preventDefault();
        void lagreOgNeste();
        return;
      }
      // F29: Ctrl+S (Cmd+S på Mac) lagrer og lar saken stå. preventDefault, ellers åpner
      // nettleseren «Lagre side som».
      if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey) && !e.altKey) {
        e.preventDefault();
        void lagreBliStaaende();
        return;
      }
      if (e.key === 'Escape') {
        if (iFelt) (mål as HTMLElement).blur();
        return;
      }
      if (iFelt || e.ctrlKey || e.metaKey || e.altKey) return;

      if (akkord.current && Date.now() - akkord.current < AKKORD_MS) {
        akkord.current = null;
        if (detalj && kanSkrive) {
          // `s` + første bokstav gjelder ALLE neste steg, også Avvist: knappen bærer «s a»
          // (DD-11), og akkorden måtte gjøre det samme. `a` alene gjør fortsatt det samme.
          const treff = VALGBARE.find((s) => statusEtikett(s, detalj.type)[0]?.toLowerCase() === e.key.toLowerCase() && kanGåTil(detalj.status, s));
          if (treff && !(treff === 'avvist' && detalj.reply)) {
            setValgtStatus(treff);
            if (treff === 'avvist') setSvarAapent(true);
          }
        }
        return;
      }

      switch (e.key) {
        case 'j':
          flytt(1);
          break;
        case 'k':
          flytt(-1);
          break;
        case 'Enter':
          aapneNotat();
          break;
        case '1':
        case '2':
        case '3': {
          const k = detalj?.dedupeCandidates?.[Number(e.key) - 1];
          if (k) setKandidat(k.id);
          break;
        }
        case 'm':
          slaaSammen();
          break;
        case 's':
          akkord.current = Date.now();
          break;
        case 'n':
          e.preventDefault();
          aapneNotat();
          break;
        case 'r':
          if (detalj && kanSkrive && !detalj.reply) {
            e.preventDefault();
            aapneSvar();
          }
          break;
        case 'a':
          // Avvist åpner svaret, men markøren går til avslagskodene først (R1.4): rekkefølgen
          // er S → kode → felt → Ctrl+Enter, og koden fyller malen i feltet.
          if (detalj && kanSkrive && !detalj.reply && kanGåTil(detalj.status, 'avvist')) {
            e.preventDefault();
            setValgtStatus('avvist');
            setSvarAapent(true);
          }
          break;
        case 'o':
          aapneSkjermbilde();
          break;
        case 'b':
          bekreftLenke();
          break;
        case 'h':
          // H3.1: «Bruk som grunnlag …» — velgerens søk får fokus straks.
          e.preventDefault();
          aapneGrunnlag();
          break;
        case 'f':
          // Registerets tittelfelt får fokus straks — bokstaven skal ikke havne der.
          e.preventDefault();
          gjoerTilKjentFeil();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detalj, kanSkrive, flytt, lagreOgNeste, lagreBliStaaende, slaaSammen, aapneSkjermbilde, bekreftLenke, aapneSvar, aapneNotat, gjoerTilKjentFeil, aapneGrunnlag]);

  const tittel = modus === 'innboks' ? 'Innboks' : 'Saker';
  const c = counts.data;
  const sistOppdatert = liste.dataUpdatedAt ? new Date(liste.dataUpdatedAt).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' }) : null;
  // Innboksen uten filter er tom: da er tomruten hele flaten (F14, konseptets skjerm 4).
  const alleVurdert = modus === 'innboks' && !liste.isPending && !liste.isError && saker.length === 0 && oppslag === undefined && !spor && !overFrist;
  // En tom innboks har ingenting som kan flytte seg: den nye saken hentes rett inn, uten stripe.
  const { refetch: hentListe } = liste;
  useEffect(() => {
    if (alleVurdert && nye.antall > 0) void hentListe();
  }, [alleVurdert, nye.antall, hentListe]);
  const velger = <RekkefolgeVelger sort={sort} onVelg={setSort} />;

  /*
    R1.3 (F13, DD-25 presisert): TO tall i toppen, begge filtre. «til vurdering» står
    alltid (DD-52: «0 til vurdering» svarer på «er jeg ferdig»); «over frist» bare over
    null (F23) — eller mens filteret er på, så veien tilbake ikke forsvinner. Median,
    klokkeslett og «uten aktivitet» er ute: varselet står på raden (DD-25), og
    sorteringen er `title` på lista (A9). Saker har ingen tellere — lista er alle statuser.
  */
  const tellere =
    modus === 'innboks' && c ? (
      <div className="panel-tellere" role="group" aria-label="Telling" aria-live="polite">
        <button type="button" className="panel-teller" aria-pressed={!overFrist} onClick={() => setOverFrist(false)}>
          <b>{c.unhandled}</b> til vurdering
        </button>
        {c.overdue > 0 || overFrist ? (
          <button type="button" className="panel-teller panel-teller-over" aria-pressed={overFrist} onClick={() => setOverFrist(true)}>
            <Ikon navn="warning-outline" />
            <b>{c.overdue}</b> over frist
          </button>
        ) : null}
      </div>
    ) : null;

  /*
    F16: innsenderen ringer med «Sak 10042». Søket er oppslaget fra det tallet til saken, og
    står først fordi det er inngangen fra support — ikke nok et filter. Saksbehandleren skal
    slippe å rense det hun fikk lest opp: «Sak » og «#» tolkes bort før tallet sendes.
  */
  const sokFelt = (
    <label className="panel-sok">
      <Ikon navn="search-outline" />
      <input ref={sokRef} type="search" value={sok} onChange={(e) => setSok(e.target.value)} placeholder="Sak 10042" inputMode="numeric" title="Saksnummeret innsenderen har" aria-label="Saksnummer" data-testid="sok" />
      {sok ? (
        <button type="button" className="panel-sok-tom" onClick={() => setSok('')} aria-label="Tøm søket" data-testid="sok-tom">
          <Ikon navn="close-circle-outline" />
        </button>
      ) : null}
    </label>
  );

  /** Sporfilteret (DD-61) finnes i begge modus: feilene først, ønskene etterpå. */
  const sporSegment = (
    <div className="panel-filter-segment" role="group" aria-label="Spor">
      {(
        [
          [undefined, 'Alle'],
          ['feil', 'Feil'],
          ['onske', 'Ønsker'],
        ] as Array<[SubmissionType | undefined, string]>
      ).map(([verdi, navn]) => (
        <button key={navn} type="button" className="panel-filter-valg" aria-pressed={spor === verdi} onClick={() => setSpor(verdi)} data-testid={`filter-spor-${verdi ?? 'alle'}`}>
          {navn}
        </button>
      ))}
    </div>
  );

  // M4.10: saken leddet gjelder er alltid med i tjenerens svar, så nummeret finnes når lista er lastet.
  const sammeInnsenderNr = sammeInnsender ? saker.find((s) => s.id === sammeInnsender)?.number : undefined;

  /** Kategorien, versjonen, kjent feil-leddet og «Samme innsender»-leddet er Sakers egne — aldri i innboksen. */
  const sakerFiltre =
    modus === 'saker' ? (
      <>
        <div className="panel-filter-segment" role="group" aria-label="Statuskategori">
          <button type="button" className="panel-filter-valg" aria-pressed={kategori === undefined} onClick={() => setKategori(undefined)}>
            Alle statuser
          </button>
          {KATEGORIER.map((k) => (
            <button key={k} type="button" className="panel-filter-valg" aria-pressed={kategori === k} onClick={() => setKategori(k)}>
              {KATEGORI[k]}
            </button>
          ))}
        </div>
        <label className="panel-filter-versjon">
          <span className="panel-etikett">Appversjon</span>
          <input value={versjon} onChange={(e) => setVersjon(e.target.value)} placeholder="2.4.0" maxLength={32} data-testid="filter-appversjon" />
        </label>
        {kjentFeil ? (
          <div className="panel-filter-segment" role="group" aria-label="Kjent feil">
            <button type="button" className="panel-filter-valg" aria-pressed="true" onClick={() =>
                setParams((p) => {
                  // Bare kjent feil-leddet går; rekkefølgen er ikke et filter (DD-74).
                  const n = new URLSearchParams(p);
                  n.delete('kjentFeil');
                  return n;
                })
              }
              data-testid="filter-kjent-feil">
              Sa «ja, jeg også» på én kjent feil <Ikon navn="close-circle-outline" />
            </button>
          </div>
        ) : null}
        {sammeInnsender ? (
          <div className="panel-filter-segment" role="group" aria-label="Samme innsender">
            {/* Saksnummeret står i leddet når saken er i lista (den er alltid med i tjenerens svar); adressen bærer bare id-en. */}
            <button type="button" className="panel-filter-valg" aria-pressed="true" onClick={() =>
                setParams((p) => {
                  const n = new URLSearchParams(p);
                  n.delete('sameSubjectAs');
                  return n;
                })
              }
              data-testid="filter-samme-innsender">
              Samme innsender{sammeInnsenderNr ? ` som ${saksnr(sammeInnsenderNr)}` : ''} <Ikon navn="close-circle-outline" />
            </button>
          </div>
        ) : null}
      </>
    ) : null;

  // Ikonet åpner feltet; samme ikon lukker det og tømmer strengen (ellers står et filter man
  // ikke ser). Markøren settes av effekten over, som i Kjente feil.
  const vekslSok = () => {
    if (sokAapen) setSok('');
    setSokAapen((v) => !v);
  };

  // Kartknappen er sidens ikon over 900 px og null under (avvik 15), men den MELDER INN
  // sidens taster i begge bredder — den står derfor i toppen uansett.
  const kartknapp = <Kartknapp tittel={modus === 'innboks' ? '40 saker uten mus' : 'Saker'} taster={KART} />;

  return (
    <>
      {smal ? (
        /*
          R4.4: under 900 px er toppen ÉN blokk (innboks-konsept v0.2, skjerm 6). Tittelen,
          søket som ikon og spor-segmentet står på samme rad; tellerne under. Toppen og
          filterlinja var to blokker på 65 + 119 px og bar 184 av de 288 px krom R4.3 målte
          (F11, R4.4s grønt-kriterium). Ingen `@media` med tall — klassen er skallets (DD-51).
        */
        <header className="panel-topp">
          <div className="panel-topp-rad">
            <h1 className="panel-h1">{tittel}</h1>
            <button type="button" className="panel-sok-knapp" aria-pressed={sokAapen} aria-label="Søk etter saksnummer" onClick={vekslSok} data-testid="sok-knapp">
              <Ikon navn="search-outline" />
            </button>
            {sporSegment}
          </div>
          {/* Feltet er den ENE linjen som kommer til mens man søker, og går igjen etterpå. */}
          {sokAapen ? sokFelt : null}
          {/* DD-74 presisert: under 900 px står rekkefølgen på tellernes linje — på egen linje
              sprengte den krom-taket på 220 px før første sak (R4.4, e2e/smal.spec.ts). */}
          <div className="panel-topp-tellere">
            {tellere}
            {velger}
          </div>
          {sakerFiltre}
          {kartknapp}
        </header>
      ) : (
        <>
          <header className="panel-topp">
            <h1 className="panel-h1">{tittel}</h1>
            {tellere}
            {kartknapp}
          </header>
          <div className="panel-filter">
            {sokFelt}
            {sporSegment}
            {sakerFiltre}
            {/* DD-74: egen linje under søket — på samme linje klemte den søkefeltet. */}
            <div className="panel-filter-linje">{velger}</div>
          </div>
        </>
      )}

      {modus === 'saker' ? <Statusflyt /> : null}

      {alleVurdert ? (
        <div className="panel-tomflate">
          <Tomrute tom alleVurdert stille={c && c.unhandled === 0 ? c.inactive : null} />
        </div>
      ) : (
        <div className="panel-todelt" data-visning={smal ? (valgtId === null ? 'liste' : 'detalj') : undefined}>
          <section className="panel-liste" aria-label={modus === 'innboks' ? 'Saker, til vurdering' : 'Saker'} title={sortertTittel(sort, 'over frist, uten aktivitet, eldst først')}>
            {oppslag === undefined && !ugyldigSok ? (
              <NyeStripe antall={nye.antall} flere={nye.flere} sort={sort} entall="nytt innspill" flertall="nye innspill" onVis={() => (sort === 'nyest' ? void liste.refetch() : setSort('nyest'))} />
            ) : null}
            {liste.isError && saker.length > 0 ? (
              <div role="alert" className="panel-melding panel-melding-advarsel">
                <Ikon navn="warning-outline" /> Kunne ikke laste. Viser siste liste{sistOppdatert ? ` fra ${sistOppdatert}` : ''}.
                <button type="button" className="panel-knapp-liten" onClick={() => void liste.refetch()}>
                  Prøv igjen
                </button>
              </div>
            ) : null}
            {liste.isPending ? (
              <div role="status" aria-busy="true" aria-label="Laster saker" className="panel-skjelett">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="panel-skjelett-rad">
                    <span className="panel-skjelett-linje" />
                    <span className="panel-skjelett-linje panel-skjelett-kort" />
                  </div>
                ))}
              </div>
            ) : liste.isError && saker.length === 0 ? (
              <div role="alert" className="panel-melding panel-melding-feil">
                <Ikon navn="warning-outline" /> Kunne ikke laste sakene. {erApiFeil(liste.error) ? liste.error.brukertekst : 'Sjekk nettet og prøv igjen.'}
                <button type="button" className="panel-knapp-liten" onClick={() => void liste.refetch()}>
                  Prøv igjen
                </button>
              </div>
            ) : ugyldigSok ? (
              <div className="panel-tom" role="status" data-testid="sok-ugyldig">
                <p className="panel-tom-tittel">«{sok.trim()}» er ikke et saksnummer.</p>
                <p className="panel-tekst">Saksnummeret er et tall fra 10001, som «Sak 10042». Det står i kvitteringen innsenderen fikk.</p>
              </div>
            ) : saker.length === 0 && oppslag !== undefined ? (
              // Et søk uten treff er ikke en tom innboks. Teksten gjentar det som ble søkt
              // etter, så saksbehandleren ser om hun tastet feil før hun sier «finnes ikke»
              // til den som ringer.
              <div className="panel-tom" role="status" data-testid="sok-tomt">
                <p className="panel-tom-tittel">Fant ingen sak med «{sok.trim()}».</p>
                <p className="panel-tekst">
                  Sjekk tallet mot det innsenderen leste opp. Nummeret står i kvitteringen hun fikk, som «Sak 10042».
                </p>
              </div>
            ) : saker.length === 0 ? (
              // Innboksen UTEN filter er tom → hele flaten er tomruten (over). Her står bare
              // en filtrert innboks og en tom kategori i Saker.
              <div className="panel-tom" role="status">
                <p className="panel-tom-tittel">{modus === 'innboks' ? 'Ingen saker med dette filteret.' : 'Ingen saker her.'}</p>
                <p className="panel-tekst">
                  {modus === 'innboks' ? (
                    <button
                      type="button"
                      className="panel-knapp-liten"
                      onClick={() => {
                        setSpor(undefined);
                        setOverFrist(false);
                      }}
                    >
                      Vis alle
                    </button>
                  ) : (
                    <>
                      Velg en annen kategori, eller se <Link to="/innboks">innboksen</Link>.
                    </>
                  )}
                </p>
              </div>
            ) : (
              <>
                <ul className="panel-rader">
                  {saker.map((s) => (
                    <Rad key={s.id} sak={s} valgt={s.id === valgtId} onSelect={() => setValgtId(s.id)} nå={nå} dempet={liste.isError} visStatus={modus === 'saker'} utkast={s.id !== valgtId && utkast.has(s.id)} />
                  ))}
                </ul>
                {liste.hasNextPage ? (
                  <div className="panel-last-mer">
                    <button type="button" className="panel-knapp-sekundaer" onClick={() => void liste.fetchNextPage()} disabled={liste.isFetchingNextPage}>
                      {liste.isFetchingNextPage ? 'Laster …' : 'Last flere'}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </section>

          <div className="panel-detalj-ramme">
            {smal && valgtId !== null ? (
              <button type="button" className="panel-knapp-sekundaer panel-tilbake" onClick={() => setValgtId(null)}>
                <Ikon navn="arrow-back-outline" /> Tilbake til lista
              </button>
            ) : null}
            {valgtId === null ? (
              <Tomrute tom={!liste.isPending && saker.length === 0} />
            ) : sak.isPending ? (
              <div role="status" aria-busy="true" aria-label="Laster sak" className="panel-skjelett">
                <span className="panel-skjelett-linje panel-skjelett-tittel" />
                <span className="panel-skjelett-linje" />
                <span className="panel-skjelett-linje panel-skjelett-kort" />
              </div>
            ) : sak.isError ? (
              <div role="alert" className="panel-melding panel-melding-feil">
                <Ikon navn="warning-outline" /> Kunne ikke laste saken. {erApiFeil(sak.error) ? sak.error.brukertekst : ''}
                <button type="button" className="panel-knapp-liten" onClick={() => void sak.refetch()}>
                  Prøv igjen
                </button>
              </div>
            ) : detalj ? (
              <Detalj
                sak={detalj}
                nå={nå}
                h={{
                  svar: { tekst: svarTekst, setTekst: setSvarTekst, mal, setMal, kode, setKode, svarRef },
                  svarAapent,
                  aapneSvar,
                  maler,
                  tenantName: me.data?.tenantName,
                  frist,
                  setFrist,
                  fristGrunn,
                  fristVedVurderes,
                  valgtStatus,
                  setValgtStatus,
                  kandidat,
                  setKandidat,
                  notat,
                  setNotat,
                  notatAapent,
                  aapneNotat,
                  fixedIn,
                  setFixedIn,
                  notatRef,
                  kanSkrive,
                  opptatt,
                  sperreGrunn,
                  lagreEtikett,
                  slaaSammenGrunn,
                  onSlaaSammen: slaaSammen,
                  onLagreOgNeste: () => void lagreOgNeste(),
                  onLagre: () => void lagreBliStaaende(),
                  // Paret finnes bare når saken får status; ellers blir den stående uansett (DD-26).
                  visLagre: statusEndres,
                  onAapneSkjermbilde: aapneSkjermbilde,
                  bildeUrl,
                  onFjernBilde: fjernBilde,
                  fjernerBilde: fjernVedlegg.isPending,
                  onBekreftLenke: bekreftLenke,
                  onFjernLenke: fjernLenke,
                  onGjoerTilKjentFeil: gjoerTilKjentFeil,
                  grunnlag,
                }}
              />
            ) : null}
          </div>
        </div>
      )}

      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}
